import { AppBar, Toolbar, useTheme } from '@mui/material';
import React from 'react';

import Websocket from '../../../components/websocket';
import { LAYOUT_CONSTANTS } from '../constants/layout-constants';
import { useLayoutCalculations } from '../hooks/use-layout-calculations';
import EnvironmentChip from './environment-chip';
import NotificationToggleButton from './notification-toggle-button';
import SecurityMessage from './security-message';
import SidebarToggleButton from './sidebar-toggle-button';
import UserProfileSection from './user-profile-section';

export type DesktopHeaderProps = {
  isSidebarOpen: boolean;
  isNotificationPanelOpen: boolean;
  onToggleSidebar: () => void;
  onToggleNotificationPanel: () => void;
};

/**
 * Desktop Header Component
 * Contains the AppBar with all header elements: sidebar toggle, environment chip,
 * websocket, security message, notification button, and user profile
 */
const DesktopHeader = ({
  isSidebarOpen,
  isNotificationPanelOpen,
  onToggleSidebar,
  onToggleNotificationPanel
}: DesktopHeaderProps) => {
  const theme = useTheme();
  const { getAppBarWidth, getAppBarMarginLeft, getAppBarMarginRight } = useLayoutCalculations();

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: getAppBarWidth(isSidebarOpen, isNotificationPanelOpen),
        ml: getAppBarMarginLeft(isSidebarOpen),
        mr: getAppBarMarginRight(isNotificationPanelOpen),
        backgroundColor: 'var(--sub-bg-color)',
        color: 'var(--text-color)',
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.standard
        })
      }}
    >
      <Toolbar sx={{ minHeight: LAYOUT_CONSTANTS.APPBAR_HEIGHT }}>
        {/* Sidebar Toggle Button */}
        <SidebarToggleButton isSidebarOpen={isSidebarOpen} onToggle={onToggleSidebar} />

        {/* Environment Chip */}
        <EnvironmentChip />

        {/* Websocket Component */}
        <Websocket />

        {/* Security Message */}
        <SecurityMessage message="" />

        {/* Notification Toggle Button */}
        <NotificationToggleButton
          isNotificationPanelOpen={isNotificationPanelOpen}
          onToggle={onToggleNotificationPanel}
        />

        {/* User Profile Section */}
        <UserProfileSection showUserName={false} />
      </Toolbar>
    </AppBar>
  );
};

export default DesktopHeader;
